import api from './api';

/**
 * Admin-Service: Tippspiele (Prediction Games) – volle Flutter-Admin-Parität.
 *
 * Endpunkte: /api/admin/predictions/* (requiresAuth + onlyAdmin)
 * Antworten kommen als { status, data: {...} } bzw. { status, message }.
 */

export interface AdminPredictionSettings {
  matchPredictionsEnabled?: boolean;
  matchDeadlineHours?: number;
  points?: { exactScore?: number; correctWinner?: number };
  specialPredictions?: {
    semiFinalEnabled?: boolean;
    semiFinalPoints?: number;
    semiFinalDeadline?: string | null;
    championEnabled?: boolean;
    championPoints?: number;
    championDeadline?: string | null;
  };
}

export interface AdminHeroSlide {
  id: string;
  image: string;
  title?: string | null;
  subtitle?: string | null;
  linkUrl?: string | null;
  buttonText?: string | null;
  ratio?: string | null;
  order?: number;
  active?: boolean;
}

export interface AdminPrize {
  id: string;
  rank: number;
  title: string;
  subtitle?: string | null;
  description?: string | null;
  image?: string | null;
}

export interface AdminLotteryPrize {
  id: string;
  title: string;
  subtitle?: string | null;
  description?: string | null;
  image?: string | null;
}

export interface AdminSponsor {
  id: string;
  title: string;
  subtitle?: string | null;
  description?: string | null;
  image?: string | null;
  linkUrl?: string | null;
  order?: number;
  active?: boolean;
}

export interface AdminPredictionGame {
  _id: string;
  id?: string;
  name: string;
  slug?: string | null;
  description?: string | null;
  tournament?: { ref?: any; flashliveId?: string; name?: string } | null;
  registrationStart?: string | null;
  registrationEnd?: string | null;
  gameStart?: string | null;
  gameEnd?: string | null;
  settings?: AdminPredictionSettings | null;
  status?: 'draft' | 'open' | 'running' | 'finished' | string;
  stats?: { totalParticipants?: number; totalPredictions?: number; evaluatedMatches?: number } | null;
  termsUrl?: string | null;
  heroSlides?: AdminHeroSlide[] | null;
  prizes?: AdminPrize[] | null;
  lotteryPrizes?: AdminLotteryPrize[] | null;
  sponsors?: AdminSponsor[] | null;
  randomizeSponsors?: boolean | null;
  adminOnly?: boolean | null;
  isActive?: boolean;
  createdAt?: string;
}

export interface AdminParticipant {
  _id: string;
  user?: { _id?: string; username?: string; email?: string; image?: string } | null;
  predictionUsername?: string | null;
  totalPoints?: number;
  registeredAt?: string;
  matchPredictions?: any[];
  semiFinalPrediction?: any;
  championPrediction?: any;
}

export interface AdminProTournamentOption {
  _id: string;
  name?: string;
  flashliveId?: string;
  country?: { name?: string } | null;
}

const gameId = (game: AdminPredictionGame | string): string =>
  typeof game === 'string' ? game : game._id;

class AdminPredictionsService {
  // ---------- Spiele ----------

  async listGames(status?: string): Promise<AdminPredictionGame[]> {
    const response = await api.get('/api/admin/predictions/games', {
      params: { limit: 100, ...(status ? { status } : {}) },
    });
    return (response.data?.data?.games as AdminPredictionGame[]) ?? [];
  }

  async getGame(id: string): Promise<AdminPredictionGame | null> {
    const response = await api.get(`/api/admin/predictions/games/${id}`);
    return (response.data?.data?.game as AdminPredictionGame) ?? null;
  }

  async createGame(payload: {
    name: string;
    tournament: { ref: string };
    description?: string;
    registrationEnd?: string | null;
    gameStart?: string | null;
    gameEnd?: string | null;
    settings?: AdminPredictionSettings;
    adminOnly?: boolean;
  }): Promise<AdminPredictionGame> {
    const response = await api.post('/api/admin/predictions/games', payload);
    return response.data?.data?.game as AdminPredictionGame;
  }

  async updateGame(id: string, payload: Partial<AdminPredictionGame>): Promise<AdminPredictionGame> {
    const response = await api.put(`/api/admin/predictions/games/${id}`, payload);
    return response.data?.data?.game as AdminPredictionGame;
  }

  async deleteGame(id: string): Promise<void> {
    await api.delete(`/api/admin/predictions/games/${id}`);
  }

  async updateStatus(id: string, status: string): Promise<AdminPredictionGame> {
    const response = await api.patch(`/api/admin/predictions/games/${id}/status`, { status });
    return response.data?.data?.game as AdminPredictionGame;
  }

  async evaluateGame(id: string): Promise<string> {
    const response = await api.post(`/api/admin/predictions/games/${id}/evaluate`);
    return response.data?.message ?? 'Auswertung gestartet';
  }

  async getParticipants(id: string): Promise<AdminParticipant[]> {
    const response = await api.get(`/api/admin/predictions/games/${id}/participants`, {
      params: { limit: 200 },
    });
    return (response.data?.data?.participants as AdminParticipant[]) ?? [];
  }

  async getStatistics(id: string): Promise<Record<string, unknown> | null> {
    const response = await api.get(`/api/admin/predictions/games/${id}/statistics`);
    return response.data?.data ?? null;
  }

  // ---------- Hero Slides ----------

  async getHeroSlides(id: string): Promise<AdminHeroSlide[]> {
    const response = await api.get(`/api/admin/predictions/games/${id}/hero-slides`);
    return (response.data?.data?.slides as AdminHeroSlide[]) ?? [];
  }

  async addHeroSlide(id: string, payload: Partial<AdminHeroSlide> & { image: string }): Promise<void> {
    await api.post(`/api/admin/predictions/games/${id}/hero-slides`, payload);
  }

  async updateHeroSlide(id: string, slideId: string, payload: Partial<AdminHeroSlide>): Promise<void> {
    await api.put(`/api/admin/predictions/games/${id}/hero-slides/${slideId}`, payload);
  }

  async deleteHeroSlide(id: string, slideId: string): Promise<void> {
    await api.delete(`/api/admin/predictions/games/${id}/hero-slides/${slideId}`);
  }

  async toggleHeroSlide(id: string, slideId: string): Promise<void> {
    await api.post(`/api/admin/predictions/games/${id}/hero-slides/${slideId}/toggle`);
  }

  async reorderHeroSlides(id: string, slideIds: string[]): Promise<void> {
    await api.put(`/api/admin/predictions/games/${id}/hero-slides/reorder`, { slideIds });
  }

  // ---------- Preise ----------

  async getPrizes(id: string): Promise<AdminPrize[]> {
    const response = await api.get(`/api/admin/predictions/games/${id}/prizes`);
    return (response.data?.data?.prizes as AdminPrize[]) ?? [];
  }

  async addPrize(id: string, payload: Partial<AdminPrize> & { rank: number; title: string }): Promise<void> {
    await api.post(`/api/admin/predictions/games/${id}/prizes`, payload);
  }

  async updatePrize(id: string, prizeId: string, payload: Partial<AdminPrize>): Promise<void> {
    await api.put(`/api/admin/predictions/games/${id}/prizes/${prizeId}`, payload);
  }

  async deletePrize(id: string, prizeId: string): Promise<void> {
    await api.delete(`/api/admin/predictions/games/${id}/prizes/${prizeId}`);
  }

  // ---------- Lospreise ----------

  async getLotteryPrizes(id: string): Promise<AdminLotteryPrize[]> {
    const response = await api.get(`/api/admin/predictions/games/${id}/lottery-prizes`);
    return (response.data?.data?.prizes as AdminLotteryPrize[]) ?? [];
  }

  async addLotteryPrize(id: string, payload: Partial<AdminLotteryPrize> & { title: string }): Promise<void> {
    await api.post(`/api/admin/predictions/games/${id}/lottery-prizes`, payload);
  }

  async updateLotteryPrize(id: string, prizeId: string, payload: Partial<AdminLotteryPrize>): Promise<void> {
    await api.put(`/api/admin/predictions/games/${id}/lottery-prizes/${prizeId}`, payload);
  }

  async deleteLotteryPrize(id: string, prizeId: string): Promise<void> {
    await api.delete(`/api/admin/predictions/games/${id}/lottery-prizes/${prizeId}`);
  }

  // ---------- Sponsoren ----------

  async getSponsors(id: string): Promise<AdminSponsor[]> {
    const response = await api.get(`/api/admin/predictions/games/${id}/sponsors`);
    return (response.data?.data?.sponsors as AdminSponsor[]) ?? [];
  }

  async addSponsor(id: string, payload: Partial<AdminSponsor> & { title: string }): Promise<void> {
    await api.post(`/api/admin/predictions/games/${id}/sponsors`, payload);
  }

  async updateSponsor(id: string, sponsorId: string, payload: Partial<AdminSponsor>): Promise<void> {
    await api.put(`/api/admin/predictions/games/${id}/sponsors/${sponsorId}`, payload);
  }

  async deleteSponsor(id: string, sponsorId: string): Promise<void> {
    await api.delete(`/api/admin/predictions/games/${id}/sponsors/${sponsorId}`);
  }

  async toggleSponsor(id: string, sponsorId: string): Promise<void> {
    await api.post(`/api/admin/predictions/games/${id}/sponsors/${sponsorId}/toggle`);
  }

  async reorderSponsors(id: string, sponsorIds: string[]): Promise<void> {
    await api.put(`/api/admin/predictions/games/${id}/sponsors/reorder`, { sponsorIds });
  }

  // ---------- Turnier-Auswahl (für Anlegen-Dialog) ----------

  async searchProTournaments(search?: string): Promise<AdminProTournamentOption[]> {
    const response = await api.get('/api/pro/tournaments', {
      params: { limit: 30, ...(search ? { search } : {}) },
    });
    return (response.data?.data?.tournaments as AdminProTournamentOption[]) ?? [];
  }

  // ---------- Bild-Upload (Slides/Preise/Sponsoren) ----------

  /** Lädt ein Bild hoch (POST /api/file, ROHES Base64) → /api/file/-URL. */
  async uploadImage(file: File): Promise<string> {
    const dataUrl: string = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden'));
      reader.readAsDataURL(file);
    });
    const base64 = dataUrl.includes('base64,') ? dataUrl.split('base64,')[1] : dataUrl;
    const response = await api.post('/api/file', { data: base64 });
    const name = response.data?.name;
    if (!name) throw new Error('Upload fehlgeschlagen');
    return `/api/file/${name}`;
  }

  // ---------- Helper ----------

  getErrorMessage(err: unknown, fallback: string): string {
    const anyErr = err as { response?: { data?: { message?: string } | string } };
    const data = anyErr?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object' && typeof data.message === 'string') return data.message;
    return fallback;
  }

  buildFileUrl(path?: string | null): string | null {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${api.defaults.baseURL}${path.startsWith('/') ? '' : '/'}${path}`;
  }

  statusLabel(status?: string | null): string {
    const map: Record<string, string> = {
      draft: 'Entwurf', open: 'Anmeldung offen', running: 'Läuft', finished: 'Beendet',
    };
    return (status && map[status]) || status || '–';
  }

  gameId = gameId;
}

const adminPredictionsService = new AdminPredictionsService();
export default adminPredictionsService;
